import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { CustomerOrderDto } from 'common/api/autogenerated/trading-gateway';

import { calculateOrderCost } from 'common/helpers';
import { useBowser, useI18n, useNotification, usePopup, useSpotOrders, useTracking } from 'common/hooks';
import { useTrades } from 'common/hooks/useTrades/useTrades.hook';

import { actionSetOrderIDIsEditing } from 'components/Orders/list/redux/orders.actions';

import { ISpotFormRequestData } from 'views/spot/dashboard/components/Form/types';

import { createOrderWithRetriesForMarket } from '../OrderForm/api';
import { getOrderByID } from './api';
import './index.css';
import { useSyncOrdersList } from 'components/Orders/list/useSyncOrdersList';
import { OrderTypeEnum } from 'components/Orders/list/types';

const SpotForm = React.lazy(
  () => import(/* webpackChunkName: "SpotForm" */ 'views/spot/dashboard/components/Form/SpotForm.container'),
);

function _delay(ms: number): Promise<void> {
  let timeoutId: NodeJS.Timeout;

  return new Promise<void>((resolve) => {
    const onResolve = () => {
      resolve();
      clearTimeout(timeoutId);
    };
    timeoutId = setTimeout(onResolve, ms);
  });
}

const EditLimitOrderComponent: React.FC<
  React.PropsWithChildren<{
    orderId: string;
    price?: number;
    onCancel?: () => void;
  }>
> = ({ orderId: id, price, onCancel: _onCancel }) => {
  const dispatch = useDispatch();
  const { cancel: cancelOrder, reload: reloadOrders } = useSpotOrders(undefined, undefined, true);
  const { hide } = usePopup();
  const { notify } = useNotification();
  const { translate } = useI18n();
  const { track } = useTracking();
  const [, syncOrdersList] = useSyncOrdersList();
  const { reload: reloadTrades } = useTrades();
  const { goBack } = useHistory();
  const { isMobile } = useBowser();

  const [orderIsCreating] = useState(false);
  const [lastSavedBaseCurrencyTotalBalance, setLastSavedBaseCurrencyTotalBalance] = useState(0);
  const [lastSavedQuotedCurrencyTotalBalance, setLastSavedQuotedCurrencyTotalBalance] = useState(0);

  useEffect(
    function noteInReduxThatOrderIsEditingNow() {
      dispatch(actionSetOrderIDIsEditing(id));
      return () => {
        dispatch(actionSetOrderIDIsEditing(null));
      };
    },
    [dispatch, id],
  );

  const targetOrderToEdit = useSelector((state: DefaultRootState) =>
    (state.ordersReducer.orders ?? []).find((x) => (x?.id ?? 0).toString() === id),
  );

  const [cachedTargetOrderToEdit, updateCachedTargetOrderToEdit] = React.useState(targetOrderToEdit);

  const targetBaseWalletAvailableBalance = useSelector(
    (state: DefaultRootState) =>
      state.walletReducer.accounts.main.find((x) => x.currency === targetOrderToEdit?.baseCurrency)?.availableBalance ??
      0,
  );

  const targetQuotedWalletAvailableBalance = useSelector(
    (state: DefaultRootState) =>
      state.walletReducer.accounts.main.find((x) => x.currency === targetOrderToEdit?.quoteCurrency)
        ?.availableBalance ?? 0,
  );

  const calculatedOrderCost = useMemo(
    () =>
      calculateOrderCost(
        targetOrderToEdit?.amount ?? 0,
        targetOrderToEdit?.price,
        targetOrderToEdit?.action,
        targetOrderToEdit?.makerCommission ?? 0,
      ),
    [
      targetOrderToEdit?.action,
      targetOrderToEdit?.amount,
      targetOrderToEdit?.makerCommission,
      targetOrderToEdit?.price,
    ],
  );

  const baseCurrencyTotalBalance = useMemo(
    () =>
      orderIsCreating
        ? lastSavedBaseCurrencyTotalBalance
        : targetBaseWalletAvailableBalance + (targetOrderToEdit?.amount ?? 0),
    [lastSavedBaseCurrencyTotalBalance, orderIsCreating, targetBaseWalletAvailableBalance, targetOrderToEdit?.amount],
  );

  const quotedCurrencyTotalBalance = useMemo(
    () =>
      orderIsCreating ? lastSavedQuotedCurrencyTotalBalance : targetQuotedWalletAvailableBalance + calculatedOrderCost,
    [calculatedOrderCost, lastSavedQuotedCurrencyTotalBalance, orderIsCreating, targetQuotedWalletAvailableBalance],
  );

  useEffect(
    function listenOrderUpdated() {
      if (targetOrderToEdit) updateCachedTargetOrderToEdit(targetOrderToEdit);
    },
    [targetOrderToEdit],
  );

  useEffect(
    function frozenBaseTotalBalanceToShowWhenOrderIsCreating() {
      if (!orderIsCreating) setLastSavedBaseCurrencyTotalBalance(baseCurrencyTotalBalance);
    },
    [baseCurrencyTotalBalance, orderIsCreating],
  );

  useEffect(
    function frozenQuotedTotalBalanceToShowWhenOrderIsCreating() {
      if (!orderIsCreating) setLastSavedQuotedCurrencyTotalBalance(quotedCurrencyTotalBalance);
    },
    [orderIsCreating, quotedCurrencyTotalBalance],
  );

  const targetPair = useMemo(
    () => cachedTargetOrderToEdit && `${cachedTargetOrderToEdit.baseCurrency}/${cachedTargetOrderToEdit.quoteCurrency}`,
    [cachedTargetOrderToEdit],
  );

  const onSubmit = React.useCallback(
    async (data: ISpotFormRequestData, order?: CustomerOrderDto) => {
      const orderFromApi = await getOrderByID(order?.id ?? 0);

      if (orderFromApi.id) {
        cancelOrder(orderFromApi.id);

        await _delay(2000);
      }

      return createOrderWithRetriesForMarket(data).then((response) => {
        notify({
          title: translate
            .toText('mobile:editlimitorder:notification-changed')
            .replace('{DIRECTION}', data.action?.toUpperCase?.())
            .replace('{PAIR}', data.asset),
        });

        track('limitOrder:edited', data);

        reloadOrders();
        reloadTrades();

        syncOrdersList(OrderTypeEnum.OPEN);

        if (isMobile) goBack();
        if (!isMobile) hide();

        return response;
      });
    },
    [cancelOrder, goBack, hide, isMobile, notify, reloadOrders, reloadTrades, syncOrdersList, track, translate],
  );

  const update = useMemo(() => {
    if (price) return { limitPrice: price };
    return undefined;
  }, [price]);

  if (!targetPair) return null;

  return (
    <div className='edit-limit-order-page__form'>
      <Suspense>
        <SpotForm symbol={targetPair} order={cachedTargetOrderToEdit} update={update} onSubmit={onSubmit} />
      </Suspense>
    </div>
  );
};
export default EditLimitOrderComponent;
